import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/home/buttons';
import { requestUpload, uploadFileToPresignedPost } from '../../service/api.service';
import { formatFileSize } from '../../util/file-util';

const MAX_FILE_SIZE_MB = 100;

const ALLOWED_MIME_PREFIX = 'audio/';
const FALLBACK_ALLOWED_EXTENSIONS = [
    'mp3',
    'wav',
    'flac',
    'm4a',
    'aac',
    'ogg',
    'opus',
    'aiff',
    'aif',
    'caf',
    'm4b',
];

const DISPLAY_FORMATS = ['MP3', 'WAV', 'FLAC', 'M4A', 'AAC', 'OGG', 'OPUS'];

const acceptAttr = [
    'audio/*',
    ...FALLBACK_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

function isLikelyAudioFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.trim().toLowerCase();
    if (extension && FALLBACK_ALLOWED_EXTENSIONS.includes(extension)) {
        return true;
    }

    const mimeType = (file.type || '').trim().toLowerCase();
    if (mimeType.startsWith(ALLOWED_MIME_PREFIX)) {
        return true;
    }

    return false;
}

export function FileInputTab() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fileSummary = useMemo(() => {
        if (!selectedFile) return t('route.home.txtUploadSelectFile');
        return `${selectedFile.name} (${formatFileSize(selectedFile.size)})`;
    }, [selectedFile, t]);

    function validateFile(): string | null {
        if (!selectedFile) {
            return t('route.home.txtUploadMissing');
        }

        if (!isLikelyAudioFile(selectedFile)) {
            return t('route.home.txtFileTypeInvalid', { formats: DISPLAY_FORMATS.join(', ') });
        }

        const sizeMb = selectedFile.size / (1024 * 1024);
        if (sizeMb > MAX_FILE_SIZE_MB) {
            return t('route.home.txtFileTooLarge', { size: MAX_FILE_SIZE_MB });
        }
        return null;
    }

    async function onBtnSubmit() {
        const validationError = validateFile();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (!selectedFile) {
            setError(t('route.home.txtCheckoutUploadFailed'));
            return;
        }

        setIsPreviewing(true);
        setError(null);

        const formData = new FormData();
        formData.append('sourceType', 'file');

        const uploadJobResponse = await requestUpload(formData);
        if (uploadJobResponse.error || !uploadJobResponse.data) {
            if (!isMountedRef.current) {
                return;
            }
            setIsPreviewing(false);
            setError(t('route.home.txtCheckoutUploadFailed'));
            return;
        }

        const { jobId, upload } = uploadJobResponse.data;
        const uploadResponse = await uploadFileToPresignedPost(upload.url, upload.fields, selectedFile);

        if (uploadResponse.error) {
            if (!isMountedRef.current) {
                return;
            }

            setIsPreviewing(false);
            setError(t('route.home.txtCheckoutUploadFailed'));
            return;
        }

        if (!isMountedRef.current) {
            return;
        }

        navigate(`/preview/${encodeURIComponent(jobId)}`);
    }

    function btnInputClicked() {
        fileInputRef.current?.click();
    }

    function onInputChanged(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0] ?? null;
        setSelectedFile(file);
        setError(null);
    }

    return (
        <div id="source-panel-file" role="tabpanel" aria-label={t('route.home.txtInputTypeFile')} className="space-y-2">
            <input ref={fileInputRef} type="file" accept={acceptAttr} className="hidden" onChange={onInputChanged} disabled={isPreviewing} />

            <Button variant={selectedFile ? 'secondary' : 'primary'} onClick={btnInputClicked} className="w-full" disabled={isPreviewing}>
                {selectedFile ? fileSummary : t('route.home.txtInputFilePick')}
            </Button>

            <p className="text-xs text-secondary text-center">{t('route.home.txtInputFileSub', { formats: DISPLAY_FORMATS.join(', '), size: MAX_FILE_SIZE_MB })}</p>

            {!!error && <p className="text-sm text-danger">{error}</p>}

            <Button onClick={onBtnSubmit} disabled={isPreviewing || !selectedFile} isLoading={isPreviewing} className="w-full mt-6">
                {t('route.home.txtPreviewAction')}
            </Button>
        </div>
    );
}
